'use client';

import Link from 'next/link';
import { useT } from '@/components/i18n-provider';
import { PenSquare } from 'lucide-react';
import { Avatar } from '@/components/avatar';
import { FollowButton } from '@/components/follow-button';
import { useAuth } from '@/components/auth-provider';
import { useState, useCallback } from 'react';
import { formatCount } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import { CardMoreMenu } from './card-more-menu';
import { Tip } from '@/components/ui/tooltip';
import type { ViewMode } from './use-discover';

interface ChannelDiscoverCardProps {
  channel: {
    id: string;
    name: string;
    slug: string;
    description?: string | null;
    logoUrl?: string | null;
    iconEmoji?: string | null;
    memberCount?: number;
    postCount?: number;
    latestPostSnippet?: string | null;
    ownerId?: string | null;
    createdById?: string | null;
    isFollowing?: boolean;
  };
  viewMode: ViewMode;
}

export function ChannelDiscoverCard({ channel, viewMode }: ChannelDiscoverCardProps) {
  const t = useT();
  const { user } = useAuth();
  const [following, setFollowing] = useState(channel.isFollowing ?? false);
  const [followLoading, setFollowLoading] = useState(false);
  const [starred, setStarred] = useState(false);
  const isOwned = !!(user && (channel.ownerId === user.id || channel.createdById === user.id));

  const handleFollow = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    haptic('light');
    setFollowLoading(true);
    try {
      const res = await fetch('/api/channels/follow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: channel.slug }),
      });
      if (res.ok) {
        const data = await res.json();
        const nowFollowing = data.following ?? !following;
        setFollowing(nowFollowing);
        window.dispatchEvent(new CustomEvent('followChanged', { detail: { type: 'channel', slug: channel.slug, following: nowFollowing } }));
        return true;
      }
      return false;
    } catch { /* ignore */ return false; }
    finally { setFollowLoading(false); }
  }, [user, channel.slug, following]);

  const handleStar = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    haptic('light');
    const next = !starred;
    setStarred(next);
    try {
      await fetch('/api/users/me/starred', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemType: 'channel', itemId: channel.id }),
      });
      window.dispatchEvent(new Event('starChanged'));
    } catch {
      setStarred(!next);
    }
  }, [user, starred, channel.id]);

  if (viewMode === 'list') {
    return (
      <Link href={`/c/${channel.slug}`} className="flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-accent/50 transition-colors group">
        <Avatar seed={channel.slug} type="channel" src={channel.logoUrl} className="h-10 w-10 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold truncate">{channel.name}</span>
            {isOwned && <Tip label={t('user.editAvatar')}><span role="button" onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/c/${channel.slug}`; }} className="cursor-pointer"><PenSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0 hover:text-foreground" /></span></Tip>}
          </div>
          <p className="text-xs text-muted-foreground truncate">//{channel.slug}</p>
          {channel.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{channel.description}</p>}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[11px] text-muted-foreground">
              {formatCount(channel.memberCount || 0)} members · {formatCount(channel.postCount || 0)} posts
            </span>
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1 ml-2">
          <CardMoreMenu starred={starred} onStar={handleStar} />
          <FollowButton
            isFollowing={following}
            loading={followLoading}
            onClick={handleFollow}
            followTargetId={channel.id}
            followTargetType="channel"
          />
        </div>
      </Link>
    );
  }

  return (
    <Link href={`/c/${channel.slug}`} className="flex flex-col p-4 rounded-xl border border-border bg-card hover:bg-accent/50 transition-all group min-w-0 h-full">
      <div className="flex items-start gap-3">
        <Avatar seed={channel.slug} type="channel" src={channel.logoUrl} className="h-14 w-14 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="text-base font-semibold truncate">{channel.name}</h3>
            {isOwned && <Tip label={t('user.editAvatar')}><span role="button" onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = `/c/${channel.slug}`; }} className="cursor-pointer"><PenSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0 hover:text-foreground" /></span></Tip>}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">//{channel.slug}</p>
        </div>
        <CardMoreMenu
          starred={starred}
          onStar={handleStar}
          following={following}
          followLoading={followLoading}
          onFollow={handleFollow}
          followLabel={{ follow: 'Follow', unfollow: 'Unfollow' }}
          className="-mt-1 -mr-1"
        />
      </div>
      <p className="text-sm text-muted-foreground mt-3 line-clamp-2 w-full">{channel.description || '\u00A0'}</p>
      <div className="flex items-center gap-4 mt-auto pt-2 text-sm text-muted-foreground">
        <span>{formatCount(channel.memberCount || 0)} members</span>
        <span>{formatCount(channel.postCount || 0)} posts</span>
      </div>
    </Link>
  );
}
